/* File download helper — production-grade with chunked retry.
 *
 * History: this is the 6th iteration of the SenrizeFlow download flow.
 * Each previous attempt fixed a specific failure mode:
 *   iter45 — switched server to async streaming (memory pressure on 1 GiB pod)
 *   iter46 — signed-URL with JWT in `?t=` so cookies aren't required
 *   iter51 — relative URL on the server response so cross-origin doesn't bite
 *   iter59 — same-origin fetch on the frontend so REACT_APP_BACKEND_URL can't
 *            point at the wrong host
 *   iter67 — chunked Range download driven by JS so any single-connection
 *            failure is invisible to the user
 *   iter146 — THIS — added THREE defensive checks after user reported that
 *            a 113 MB video downloaded successfully but the saved file was
 *            corrupt (correct length, wrong bytes):
 *              1. Verify Content-Range header of every chunk matches the
 *                 range we asked for — catches server-side range mismatches.
 *              2. Cache-bust every chunk URL with `?_r=<start>-<ts>` so no
 *                 browser or CDN cache can serve a stale range response.
 *              3. Compute MD5 of the assembled blob and compare to the
 *                 server's stored md5. Mismatch → fall back to single-shot
 *                 direct download instead of handing the user bad bytes.
 */
import { toast } from "sonner";
import SparkMD5 from "spark-md5";

const FILE_DOWNLOAD_PATTERN = /\/api\/files\/([0-9a-f-]{8,})\/(?:download|stream)(?:\?|$)/i;
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024;   // 8 MB chunks — small enough to clear any proxy timeout
const LARGE_FILE_THRESHOLD = 8 * 1024 * 1024; // files >= 8 MB use chunked path
const MAX_RETRIES_PER_CHUNK = 4;

function extractFileId(url) {
  if (!url) return null;
  const m = (url || "").match(FILE_DOWNLOAD_PATTERN);
  return m ? m[1] : null;
}

function toSameOriginPath(url) {
  if (!url) return "/";
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + u.search + u.hash;
  } catch {
    return url.startsWith("/") ? url : `/${url}`;
  }
}

function clickDownloadLink(href, suggestedFilename, revokeAfter) {
  const a = document.createElement("a");
  a.href = href;
  if (suggestedFilename) a.download = suggestedFilename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { a.remove(); } catch { /* ignore */ }
    if (revokeAfter) {
      try { URL.revokeObjectURL(href); } catch { /* ignore */ }
    }
  }, 1500);
}

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

/** Probe the file's total size via a 1-byte Range request. We use Range
 *  instead of HEAD because the streaming endpoint always GETs (HEAD would
 *  require a separate route). 1 byte is negligible. */
async function probeFileSize(fileId) {
  const r = await fetch(`/api/files/${fileId}/download`, {
    method: "GET",
    headers: { Range: "bytes=0-0" },
    credentials: "include",
    cache: "no-store",
  });
  if (!r.ok && r.status !== 206) {
    throw new Error(`probe failed: HTTP ${r.status}`);
  }
  // Iter107 — only trust Content-Range when the server actually replied
  // 206 Partial Content. A 200 means Range was ignored (Cloudflare cache,
  // legacy proxy, etc.) and Content-Length is the WHOLE file — which
  // would tell the chunked loop to download the file repeatedly. In that
  // case we throw so the caller falls back to the single-shot `<a download>`
  // path that has no concatenation risk.
  if (r.status === 206) {
    const cr = r.headers.get("Content-Range");
    if (cr) {
      const m = cr.match(/\/(\d+)\s*$/);
      if (m) {
        await r.body?.cancel?.();
        return parseInt(m[1], 10);
      }
    }
    // 206 without parseable Content-Range — bail.
    await r.body?.cancel?.();
    throw new Error("probe: 206 without Content-Range");
  }
  // status === 200 → Range was ignored; we cannot safely chunk. Drain
  // the body so the connection isn't kept open.
  await r.arrayBuffer();
  throw new Error("probe: server did not honour Range (HTTP 200)");
}

/** Fetch a single byte range with retry + exponential backoff. */
async function fetchChunk(fileId, start, end, onAttempt) {
  let lastErr = null;
  const expected = end - start + 1;
  for (let attempt = 1; attempt <= MAX_RETRIES_PER_CHUNK; attempt++) {
    try {
      onAttempt?.(attempt);
      // Iter146 — cache-bust every chunk URL. Even though the backend now
      // sends `Cache-Control: no-store`, some intermediate CDN nodes may
      // have cached a 206 response from before that header change was
      // deployed. A unique `?_r=<start>-<ts>` per request forces a fresh
      // upstream fetch every time.
      const bust = `?_r=${start}-${Date.now()}`;
      const r = await fetch(`/api/files/${fileId}/download${bust}`, {
        method: "GET",
        headers: { Range: `bytes=${start}-${end}` },
        credentials: "include",
        cache: "no-store",
      });
      if (r.status !== 206 && r.status !== 200) {
        throw new Error(`HTTP ${r.status}`);
      }
      if (r.status === 206) {
        // Iter146 — CRITICAL: verify the server actually returned the range
        // we asked for. A byte-length match alone isn't enough: if the
        // server (or an intermediate cache) returned a DIFFERENT range with
        // the same length, we'd silently concatenate mis-aligned bytes and
        // hand the user a corrupt MP4. This is the exact failure mode the
        // user reported ("113 MB downloaded, correct size, won't play").
        const cr = r.headers.get("Content-Range");
        if (cr) {
          const m = cr.match(/bytes\s+(\d+)-(\d+)\/(\d+)/i);
          if (!m) throw new Error(`chunk ${start}-${end}: unparseable Content-Range "${cr}"`);
          const gotStart = parseInt(m[1], 10);
          const gotEnd = parseInt(m[2], 10);
          if (gotStart !== start || gotEnd !== end) {
            throw new Error(
              `chunk range mismatch: asked ${start}-${end}, got ${gotStart}-${gotEnd}`
            );
          }
        }
      }
      const buf = await r.arrayBuffer();
      if (r.status === 206) {
        if (buf.byteLength !== expected) {
          throw new Error(`short read: got ${buf.byteLength} of ${expected}`);
        }
        return buf;
      }
      // status === 200 — server didn't honour Range. We can only safely
      // use the response if it represents the entire file and our chunk
      // was the FIRST chunk; in all other cases bail so the caller falls
      // back to a single-shot download instead of concatenating.
      throw new Error("range-not-honoured");
    } catch (e) {
      lastErr = e;
      // Backoff: 0.3s, 0.8s, 2s, 5s
      const backoff = [300, 800, 2000, 5000][attempt - 1] || 5000;
      await sleep(backoff);
    }
  }
  throw new Error(`chunk ${start}-${end} failed after ${MAX_RETRIES_PER_CHUNK} retries: ${lastErr?.message || lastErr}`);
}

/** Chunked download for large files. Builds a Blob from N range
 *  requests, then triggers save via `<a download>`.
 *
 *  Iter150 — parallelised: 4 chunks fly in flight at once, giving
 *  ~3-4× real-world throughput on fast connections (was serial, so
 *  every chunk paid full RTT before the next request went out).
 *  Ordering is preserved by writing into a positional array indexed
 *  by chunk-index; MD5 is computed AFTER all chunks land so streaming
 *  integrity still holds.
 */
async function chunkedDownload(fileId, suggestedFilename, onProgress) {
  const totalSize = await probeFileSize(fileId);
  const chunkCount = Math.ceil(totalSize / CHUNK_SIZE_BYTES);
  const parts = new Array(chunkCount);
  let received = 0;
  const CONCURRENCY = 4;

  let cursor = 0;
  const worker = async () => {
    while (true) {
      const idx = cursor++;
      if (idx >= chunkCount) return;
      const start = idx * CHUNK_SIZE_BYTES;
      const end = Math.min(start + CHUNK_SIZE_BYTES - 1, totalSize - 1);
      // eslint-disable-next-line no-await-in-loop
      const buf = await fetchChunk(fileId, start, end);
      parts[idx] = buf;
      received += buf.byteLength;
      onProgress?.(received, totalSize);
    }
  };
  const workers = Array.from({ length: Math.min(CONCURRENCY, chunkCount) }, () => worker());
  await Promise.all(workers);

  // Integrity: hash the assembled bytes in-order.
  const md5 = new SparkMD5.ArrayBuffer();
  for (let i = 0; i < chunkCount; i += 1) {
    md5.append(parts[i]);
  }
  const localMd5 = md5.end();

  // Iter146 — cross-check with the server-side MD5 (Iter139 populated the
  // `md5` field on every Drive-backed record). Mismatch = the transit
  // pipeline flipped bytes somewhere between Drive and the browser →
  // don't hand a broken file to the user; signal a hard failure so the
  // caller can fall back to a single-shot browser download.
  try {
    const meta = await fetch(`/api/files/${fileId}/drive-checksum`, {
      credentials: "include", cache: "no-store",
    });
    if (meta.ok) {
      const j = await meta.json();
      const serverMd5 = (j.drive_md5 || j.stored_md5 || "").toLowerCase();
      if (serverMd5 && serverMd5 !== localMd5.toLowerCase()) {
        throw new Error(
          `Downloaded file failed MD5 check (expected ${serverMd5.slice(0, 8)}…, ` +
          `got ${localMd5.slice(0, 8)}…). Falling back to direct download.`
        );
      }
    }
    // If /drive-checksum returns non-2xx (file isn't on Drive, or user
    // isn't admin), we simply skip verification — the length + Range
    // checks in fetchChunk are still enforced.
  } catch (verifyErr) {
    // Only propagate real MD5 mismatch errors; ignore network errors on
    // the verification endpoint itself.
    if (String(verifyErr.message || "").includes("MD5 check")) throw verifyErr;
    console.warn("[download] MD5 verification skipped:", verifyErr);
  }

  const blob = new Blob(parts);
  const objUrl = URL.createObjectURL(blob);
  clickDownloadLink(objUrl, suggestedFilename, /*revokeAfter=*/true);
}

/** Light "downloading…" toast for chunked transfers so the user knows
 *  something is happening on a 200 MB file. */
function showDownloadToast(filename) {
  return toast.loading(`Preparing ${filename || "download"}…`, {
    duration: Infinity,
  });
}

/**
 * Public API — download a file. Picks the optimal strategy by file size.
 *
 * @param {string} url  absolute or relative URL of the file endpoint
 * @param {string} [suggestedFilename]  optional `<a download>` filename
 */
export async function downloadFile(url, suggestedFilename) {
  if (!url) return;

  const fid = extractFileId(url);
  if (!fid) {
    // Non-file endpoint (invoice PDF, receipt PDF, portal export) — keep
    // the simple same-origin `<a download>` path. These are always small.
    let pathAndQuery = toSameOriginPath(url);
    if (!/[?&]download=/.test(pathAndQuery)) {
      pathAndQuery += (pathAndQuery.includes("?") ? "&" : "?") + "download=1";
    }
    clickDownloadLink(`${window.location.origin}${pathAndQuery}`, suggestedFilename);
    return;
  }

  // Probe size first so we know whether to use the chunked path.
  let totalSize = 0;
  try {
    totalSize = await probeFileSize(fid);
  } catch {
    // Fallback: same-origin direct download (cookie-auth, single shot).
    let pathAndQuery = toSameOriginPath(url);
    if (!/[?&]download=/.test(pathAndQuery)) {
      pathAndQuery += (pathAndQuery.includes("?") ? "&" : "?") + "download=1";
    }
    clickDownloadLink(`${window.location.origin}${pathAndQuery}`, suggestedFilename);
    return;
  }

  if (totalSize < LARGE_FILE_THRESHOLD) {
    // Small file — single same-origin `<a download>` is best (uses Chrome's
    // native download manager, supports resume, no JS memory cost).
    let pathAndQuery = toSameOriginPath(url);
    if (!/[?&]download=/.test(pathAndQuery)) {
      pathAndQuery += (pathAndQuery.includes("?") ? "&" : "?") + "download=1";
    }
    clickDownloadLink(`${window.location.origin}${pathAndQuery}`, suggestedFilename);
    return;
  }

  // Large file — chunked path with progress + retry per chunk.
  const toastId = showDownloadToast(suggestedFilename);
  try {
    await chunkedDownload(fid, suggestedFilename, (received, total) => {
      const pct = Math.round((received / total) * 100);
      toast.loading(`Downloading ${suggestedFilename || "file"} · ${pct}%`,
        { id: toastId, duration: Infinity });
    });
    toast.success(`Downloaded ${suggestedFilename || "file"}`, { id: toastId });
  } catch (e) {
    console.error("[downloadFile] chunked path failed:", e);
    toast.error(`Download failed: ${e.message || e}`, { id: toastId });
    // Last-ditch — open in a new tab so the user can save via the browser
    // chrome's "Save as" menu.
    let pathAndQuery = toSameOriginPath(url);
    if (!/[?&]download=/.test(pathAndQuery)) {
      pathAndQuery += (pathAndQuery.includes("?") ? "&" : "?") + "download=1";
    }
    window.open(`${window.location.origin}${pathAndQuery}`, "_blank", "noopener,noreferrer");
  }
}
